import { Link } from 'react-router-dom';
import {
  Ban,
  ChevronRight,
  FileText,
  Gauge,
  Inbox,
  KeyRound,
  Lock,
  Mail,
  Package,
  Plug,
  ScrollText,
  Tag,
  Users,
  Wallet,
} from 'lucide-react';
import { messages, UserRoles, type UserRole } from '@nesc/shared';
import { useAuth } from '@/lib/auth';
import { Card } from '@/components/ui/card';
import { PageHeader } from '@/components/common/page';

type SettingsItem = {
  to: string;
  label: string;
  icon: typeof Package;
  minRole: UserRole;
  description: string;
};

/** S70 設定トップ。項目を用途ごとにまとめ、PC では 2 列に並べる */
const GROUPS: { id: string; title: string; items: SettingsItem[] }[] = [
  {
    id: 'sales',
    title: '営業の準備',
    items: [
      { to: '/settings/products', label: messages.settings.products, icon: Package, minRole: 'manager', description: '商材・強み・事例・禁止表現' },
      { to: '/settings/templates', label: messages.settings.templates, icon: FileText, minRole: 'manager', description: '差し込み項目つきの定型文面' },
      { to: '/settings/tags', label: messages.settings.tags, icon: Tag, minRole: 'manager', description: 'タグの作成・色・分類基準（自動タグ付け）' },
      { to: '/settings/suppressions', label: messages.settings.suppressions, icon: Ban, minRole: 'member', description: '営業禁止・配信停止・既存顧客' },
    ],
  },
  {
    id: 'mail',
    title: 'メール',
    items: [
      { to: '/settings/mail', label: messages.settings.mailSend, icon: Mail, minRole: 'admin', description: '差出人・送信方法・署名・効果測定' },
      { to: '/settings/mail-receive', label: messages.settings.mailReceive, icon: Inbox, minRole: 'admin', description: '返信を自動で取り込む受信箱' },
      { to: '/settings/google-workspace', label: messages.settings.googleWorkspace, icon: KeyRound, minRole: 'admin', description: 'メーラーで使う受信箱を OAuth で接続' },
    ],
  },
  {
    id: 'organization',
    title: '組織と連携',
    items: [
      { to: '/settings/users', label: messages.settings.users, icon: Users, minRole: 'admin', description: '招待・権限・停止' },
      { to: '/settings/integrations', label: messages.settings.integrations, icon: Plug, minRole: 'admin', description: 'AI・検索・日程調整・ストレージ' },
      { to: '/settings/limits', label: messages.settings.limits, icon: Gauge, minRole: 'admin', description: '利用上限・クロール・観測期間' },
    ],
  },
  {
    id: 'records',
    title: '利用状況と記録',
    items: [
      { to: '/usage', label: messages.usage.title, icon: Wallet, minRole: 'manager', description: 'API 利用量と費用' },
      { to: '/logs', label: messages.logs.title, icon: ScrollText, minRole: 'manager', description: '操作・収集・送信・AI 実行' },
    ],
  },
];

/** 権限が足りないときに出す理由（管理者のみ／マネージャー以上） */
const requiredRoleNote = (minRole: UserRole) =>
  minRole === 'admin'
    ? `${UserRoles.label(minRole)}のみ利用できます`
    : `${UserRoles.label(minRole)}以上が利用できます`;

const SettingsRow = ({ item, allowed }: { item: SettingsItem; allowed: boolean }) => {
  const body = (
    <>
      <item.icon aria-hidden className="size-5 shrink-0 text-foreground-muted" />
      <span className="min-w-0 flex-1">
        <span className="block text-body font-semibold">{item.label}</span>
        <span className="block text-note text-foreground-muted">{item.description}</span>
        {!allowed && (
          <span className="mt-0.5 flex items-center gap-1 text-note text-foreground-muted">
            <Lock aria-hidden className="size-3.5 shrink-0" />
            {requiredRoleNote(item.minRole)}
          </span>
        )}
      </span>
    </>
  );

  // 権限が足りない項目は非表示ではなく非活性＋理由表示にする
  if (!allowed) {
    return (
      <div
        aria-disabled="true"
        title={messages.settings.noPermission}
        className="flex min-h-touch cursor-not-allowed items-center gap-3 px-4 py-3 opacity-60"
      >
        {body}
      </div>
    );
  }

  return (
    <Link
      to={item.to}
      className="flex min-h-touch items-center gap-3 px-4 py-3 transition-colors duration-hover hover:bg-muted"
    >
      {body}
      <ChevronRight aria-hidden className="size-5 shrink-0 text-foreground-subtle" />
    </Link>
  );
};

export const SettingsPage = () => {
  const { hasRole, organization } = useAuth();

  return (
    <>
      <PageHeader
        title={messages.settings.title}
        description={organization ? `組織: ${organization.name}` : undefined}
      />

      <div className="grid items-start gap-6 lg:grid-cols-2">
        {GROUPS.map((group) => (
          <section key={group.id} aria-labelledby={`settings-${group.id}`}>
            <h2 id={`settings-${group.id}`} className="mb-2 text-h3">
              {group.title}
            </h2>
            <Card className="overflow-hidden">
              <ul className="divide-y divide-border">
                {group.items.map((item) => (
                  <li key={item.to}>
                    <SettingsRow item={item} allowed={hasRole(item.minRole)} />
                  </li>
                ))}
              </ul>
            </Card>
          </section>
        ))}
      </div>
    </>
  );
};
